import { useEffect, useState } from "react";
import { api } from "../api";
import type { StatsSummary } from "@shared/types";
import { formatSim, heatColor } from "../lib/heat";
import { motion } from "framer-motion";

export function Stats() {
  const [data, setData] = useState<StatsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.stats()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "error"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-ink-400 text-center py-12">loading stats…</p>;
  if (error) return <p className="text-red-400 text-center py-12">{error}</p>;
  if (!data) return null;

  const winRate = data.totalRounds ? (data.totalWins / data.totalRounds) * 100 : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-6">
      <h1 className="font-display text-3xl">stats</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="rounds" value={String(data.totalRounds)} />
        <StatCard label="wins" value={String(data.totalWins)} />
        <StatCard label="win rate" value={`${winRate.toFixed(0)}%`} />
        <StatCard
          label="best similarity"
          value={formatSim(data.bestSimilarity)}
          color={heatColor(data.bestSimilarity)}
        />
      </div>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-sm uppercase tracking-wider text-ink-400 mb-3">by language</h2>
        <div className="flex flex-col gap-2">
          {Object.entries(data.byLanguage).map(([lang, v]) => (
            <div key={lang} className="flex items-center gap-3 text-sm">
              <span className="w-12 font-mono text-ink-200 uppercase">{lang}</span>
              <span className="text-ink-100">{v.rounds} rounds</span>
              <span className="text-ink-400">·</span>
              <span className="text-ink-100">{v.wins} wins</span>
            </div>
          ))}
          {Object.keys(data.byLanguage).length === 0 && <p className="text-ink-400 text-sm">no rounds yet.</p>}
        </div>
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-sm uppercase tracking-wider text-ink-400 mb-3">recent rounds</h2>
        <div className="flex flex-col gap-2 text-sm">
          {data.recent.map((r) => (
            <div key={r.id} className="flex items-center gap-3">
              <span className="font-mono text-xs text-ink-400 w-24">
                {new Date(r.endedAt ?? r.startedAt).toLocaleString()}
              </span>
              <span className="px-2 py-0.5 rounded bg-white/5 font-mono text-xs uppercase">{r.language}</span>
              <span className="px-2 py-0.5 rounded bg-white/5 font-mono text-xs uppercase">{r.mode}</span>
              <span className="text-ink-300">{r.guessCount} guesses</span>
              <span className="ml-auto font-mono tabular-nums" style={{ color: heatColor(r.bestSimilarity) }}>
                {formatSim(r.bestSimilarity)}
              </span>
              <span className={`w-12 text-right text-xs ${r.won ? "text-emerald-400" : "text-ink-500"}`}>
                {r.won ? "WIN" : "—"}
              </span>
            </div>
          ))}
          {data.recent.length === 0 && <p className="text-ink-400">play your first round!</p>}
        </div>
      </section>
    </motion.div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="glass rounded-2xl p-4">
      <div className="text-xs uppercase tracking-wider text-ink-400">{label}</div>
      <div className="font-mono text-3xl tabular-nums mt-1" style={color ? { color } : undefined}>
        {value}
      </div>
    </div>
  );
}
